import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import { useAudit } from '@/hooks/useNotifications';
import type { AuditEntry } from '@/types/notification';

import styles from './AuditPanel.module.css';
import { motivoLegivel } from '@/services/erroLegivel';

const RESOURCE_TYPES = [
  { value: '', label: 'Todos' },
  { value: 'article', label: 'Artigos' },
  { value: 'workflow', label: 'Workflows' },
  { value: 'template', label: 'Templates' },
];

const LIMITS = [25, 50, 100, 200];

function actorBadgeTone(actorType: string): 'info' | 'warn' | 'neutral' {
  if (actorType === 'mcp_agent') return 'warn';
  if (actorType === 'user') return 'info';
  return 'neutral';
}

function actionTone(action: string): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (action.includes('error') || action.includes('delete')) return 'crit';
  if (action.includes('update') || action.includes('activate')) return 'warn';
  if (action.includes('create') || action.includes('trigger') || action.includes('test'))
    return 'ok';
  return 'neutral';
}

export function AuditPanel() {
  const { t } = useTranslation(['config', 'common']);
  const [resourceType, setResourceType] = useState('');
  const [limit, setLimit] = useState(50);
  const [actionSearch, setActionSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const {
    data: entries,
    isLoading,
    error,
    refetch,
  } = useAudit({
    limit,
    resource_type: resourceType || undefined,
  });

  const filtered = useMemo(() => {
    if (!entries) return [];
    if (!actionSearch.trim()) return entries;
    const q = actionSearch.toLowerCase();
    return entries.filter(
      (e) =>
        e.action.toLowerCase().includes(q) ||
        (e.resource_type && e.resource_type.toLowerCase().includes(q)) ||
        (e.actor_id && e.actor_id.toLowerCase().includes(q)),
    );
  }, [entries, actionSearch]);

  const stats = useMemo(() => {
    if (!entries) return { total: 0, byActor: new Map<string, number>() };
    const byActor = new Map<string, number>();
    entries.forEach((e) => {
      const key = `${e.actor_type}:${e.actor_id}`;
      byActor.set(key, (byActor.get(key) ?? 0) + 1);
    });
    return { total: entries.length, byActor };
  }, [entries]);

  return (
    <section>
      <header className={styles.head}>
        <Link to="/config" className={styles.back}>
          ← Configurações
        </Link>
        <h1 className={styles.title}>{t('config:audit.title')}</h1>
        <Badge tone="info" size="sm">
          Axioma V
        </Badge>
        {entries && (
          <span className={styles.totalBadge}>
            {filtered.length} / {stats.total} entradas
          </span>
        )}
      </header>

      <p className={styles.intro}>
        Toda mutação no sistema é registrada aqui: edições de artigos, criação de overrides, testes
        de prompt, disparos de workflow, etc. Inclui ações via REST API e via MCP.
      </p>

      <div className={styles.toolbar}>
        <input
          type="search"
          placeholder="Buscar action (ex: workflow.trigger)…"
          value={actionSearch}
          onChange={(e) => setActionSearch(e.target.value)}
          className={styles.searchInput}
        />

        <label className={styles.fieldInline}>
          <span className={styles.fieldLabel}>Recurso</span>
          <select
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
            className={styles.select}
          >
            {RESOURCE_TYPES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.fieldInline}>
          <span className={styles.fieldLabel}>Limite</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className={styles.select}
          >
            {LIMITS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>

        <button className={styles.refreshBtn} onClick={() => refetch()}>
          Refresh
        </button>
      </div>

      {isLoading && <p className={styles.muted}>Carregando…</p>}
      {error && <p className={styles.error}>{motivoLegivel(error)}</p>}

      {entries && filtered.length === 0 && (
        <div className={styles.empty}>Sem entradas com estes filtros.</div>
      )}

      <ul className={styles.list}>
        {filtered.map((entry) => (
          <AuditRow
            key={entry.id}
            entry={entry}
            expanded={expandedId === entry.id}
            onToggle={() => setExpandedId((prev) => (prev === entry.id ? null : entry.id))}
          />
        ))}
      </ul>
    </section>
  );
}

interface RowProps {
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}

function AuditRow({ entry, expanded, onToggle }: RowProps) {
  return (
    <li className={`${styles.row} ${expanded ? styles.rowExpanded : ''}`}>
      <button className={styles.rowMain} onClick={onToggle}>
        <span className={styles.ts}>
          {formatarDataHora(entry.ts, {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </span>
        <Badge tone={actionTone(entry.action)} size="xs">
          {entry.action}
        </Badge>
        <Badge tone={actorBadgeTone(entry.actor_type)} size="xs">
          {entry.actor_type}
        </Badge>
        <span className={styles.resource}>
          {entry.resource_type}/{entry.resource_id}
        </span>
        {entry.ip && <span className={styles.metaItem}>ip: {entry.ip}</span>}
        <span className={styles.expander}>{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div className={styles.detail}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Payload</span>
            <pre className={styles.code}>{JSON.stringify(entry.payload, null, 2)}</pre>
          </div>
          {entry.result && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Result</span>
              <pre className={styles.code}>{JSON.stringify(entry.result, null, 2)}</pre>
            </div>
          )}
          {entry.user_agent && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>User-Agent</span>
              <span className={styles.detailValue}>{entry.user_agent}</span>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
